import React from 'react';
import { Card, Space, Button, Tag, Typography } from 'antd';
import { EyeOutlined, DollarOutlined, CalendarOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FinanceRecordWithDetails, PaymentNature, ExpenseType, EntityType } from '../../../services/finance';
import './FinanceCardView.css';

const { Text } = Typography;

const EXPENSE_TYPE_MAP: Record<ExpenseType, string> = {
  [ExpenseType.CONSULTATION_FEE]: '咨询费',
  [ExpenseType.LAWYER_FEE]: '律师费',
  [ExpenseType.CONSULTANT_FEE]: '顾问费',
  [ExpenseType.SERVICE_FEE]: '服务费',
  [ExpenseType.TRAVEL_FEE]: '差旅费',
  [ExpenseType.OTHER]: '其他',
};

const PAYMENT_NATURE_MAP: Record<PaymentNature, string> = {
  [PaymentNature.RECEIVED]: '已收款',
  [PaymentNature.RECEIVABLE]: '应收款',
  [PaymentNature.REFUND]: '退款',
};

const ENTITY_TYPE_MAP: Record<EntityType, string> = {
  [EntityType.LEAD]: '线索',
  [EntityType.CASE]: '案件',
};

interface FinanceCardViewProps {
  records: FinanceRecordWithDetails[];
  loading: boolean;
  onViewEntity?: (entityType: EntityType, entityId: number) => void;
}

const FinanceCardView: React.FC<FinanceCardViewProps> = ({
  records,
  loading,
  onViewEntity,
}) => {
  const getPaymentNatureColor = (nature: PaymentNature) => {
    const colorMap: Record<PaymentNature, string> = {
      [PaymentNature.RECEIVED]: 'success',
      [PaymentNature.RECEIVABLE]: 'warning',
      [PaymentNature.REFUND]: 'error',
    };
    return colorMap[nature] || 'default';
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>加载中...</div>;
  }

  if (records.length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>暂无数据</div>;
  }

  return (
    <div className="finance-card-container">
      {records.map(record => {
        const amount = typeof record.amount === 'string' ? parseFloat(record.amount) : record.amount;
        const isRefund = record.payment_nature === PaymentNature.REFUND;
        
        return (
          <Card key={record.id} className="finance-card">
            <div className="finance-card-header">
              <div>
                <Text strong style={{ fontSize: 16 }}>
                  {record.case_name || record.contact_name || '未命名'}
                </Text>
                <div style={{ marginTop: 4 }}>
                  <Tag color="blue" style={{ margin: 0 }}>
                    {ENTITY_TYPE_MAP[record.entity_type]}
                  </Tag>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <Text
                  strong
                  style={{
                    fontSize: 18,
                    color: isRefund ? '#ff4d4f' : '#52c41a',
                  }}
                >
                  {isRefund ? '-' : '+'}￥{amount.toLocaleString()}
                </Text>
                <div style={{ marginTop: 4 }}>
                  <Tag color={getPaymentNatureColor(record.payment_nature)} style={{ margin: 0 }}>
                    {PAYMENT_NATURE_MAP[record.payment_nature]}
                  </Tag>
                </div>
              </div>
            </div>

            <div className="finance-card-body">
              <div className="finance-card-item">
                <FileTextOutlined style={{ color: '#1890ff' }} />
                <Text>{EXPENSE_TYPE_MAP[record.expense_type]}</Text>
              </div>

              {record.payment_time && (
                <div className="finance-card-item">
                  <CalendarOutlined style={{ color: '#52c41a' }} />
                  <Text type="secondary">
                    {dayjs(record.payment_time).format('YYYY-MM-DD HH:mm')}
                  </Text>
                </div>
              )}

              {record.responsible_person_name && (
                <div className="finance-card-item">
                  <Text type="secondary">负责人：{record.responsible_person_name}</Text>
                </div>
              )}

              {record.remark && (
                <div className="finance-card-item">
                  <Text type="secondary" ellipsis>备注：{record.remark}</Text>
                </div>
              )}
            </div>

            {onViewEntity && record.entity_id && (
              <div className="finance-card-footer">
                <Button
                  type="link"
                  size="small"
                  icon={<EyeOutlined />}
                  onClick={() => onViewEntity(record.entity_type, record.entity_id)}
                >
                  查看详情
                </Button>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default FinanceCardView;
